import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueries } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, ExternalLink, PanelRightOpen, PenLine } from 'lucide-react'
import { api, type RedeemApp, type RedeemBatch, type ScheduleItem } from '@/api/client'
import { usePostActions } from '@/lib/postActions'
import { qk, useRedeemApps, useSchedule } from '@/lib/queries'
import { statusOf, type Tone } from '@/lib/status'
import { dayKey, fromLocalInput, pastSlotError } from '@/lib/time'
import { cn } from '@/lib/utils'
import {
  Button,
  ErrorState,
  IconButton,
  MonthCalendar,
  PageHeader,
  Select,
  SlotPicker,
  SourceTag,
  StatusPill,
  TimePicker,
  TonePill,
  WeekStrip,
  describeDay,
  errorText,
  parseDay,
  platformName,
  useIsDesktop,
  useQueryParam,
  useToast,
  useWidePage,
  weekDays,
  type CalendarEntry,
  type Source,
} from '@/ui'
import { DeskPostForm } from './DeskPostForm'
import { DraftForm } from './DraftForm'
import { ScheduleFilterRow, useScheduleFilters } from './ScheduleList'
import {
  abilities,
  channelLabel,
  headline,
  isWaiting,
  itemTime,
  ownerName,
  refOf,
  sourceOf,
  useOpenDetail,
  usePostCommands,
  whereLabel,
  WritePostDialog,
} from './shared'

const pad = (n: number) => String(n).padStart(2, '0')
const timeOf = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`

type MarkerKind = 'expiry' | 'free_until' | 'ended'

/** One thing on one day: a post going out (or gone, or failed), or a date the
 *  codes themselves set — the day a batch stops redeeming, the day a free app
 *  turns paid again, the day a giveaway was closed. */
interface Entry extends CalendarEntry {
  detail: string
  item?: ScheduleItem
  app?: RedeemApp
  marker?: MarkerKind
}

function postEntry(it: ScheduleItem): Entry | null {
  const t = itemTime(it)
  if (!t) return null
  const d = new Date(t)
  const def = statusOf('post', it.status, { scheduledAt: it.scheduled_at })
  const owner = ownerName(it)
  return {
    key: `${it.kind}-${it.id}`,
    day: dayKey(d),
    time: timeOf(d),
    label: owner || platformName(it.platform),
    title: `${platformName(it.platform)} · ${owner} — ${headline(it)}`,
    tone: def.tone,
    source: sourceOf(it),
    platform: it.platform,
    detail: headline(it, 80),
    item: it,
  }
}

/** "Reddit · r/SideProject", "Threads · @noveldesk.app"; a campaign post
 *  whose account is left to the campaign names the campaign instead of
 *  repeating the platform ("Threads · Threads"). */
function dayPanelWhere(it: ScheduleItem): string {
  const name = platformName(it.platform)
  const where = channelLabel(it)
  return `${name} · ${where === name ? ownerName(it) || name : where}`
}

function markerEntries(apps: RedeemApp[], batches: Record<number, RedeemBatch[]>, appId: number): Entry[] {
  const out: Entry[] = []
  const tone = (k: MarkerKind): Tone => statusOf('marker', k).tone
  for (const a of apps) {
    if (appId && a.id !== appId) continue
    if (a.status === 'archived') continue
    if (a.expires_on) {
      const title = `코드 만료 · ${a.name}`
      out.push({ key: `exp-${a.id}`, day: a.expires_on, time: '', label: title, title, tone: tone('expiry'), detail: '앱에 적힌 만료일', app: a, marker: 'expiry' })
    }
    for (const b of batches[a.id] ?? []) {
      // A batch with its own date is the one that matters: those are the codes
      // that stop working. A spent batch has nothing left to expire.
      if (!b.expires_on || b.expires_on === a.expires_on || b.available === 0) continue
      const title = `코드 만료 · ${a.name}`
      out.push({
        key: `batch-${b.id}`,
        day: b.expires_on,
        time: '',
        label: title,
        title,
        tone: tone('expiry'),
        detail: `${b.label || `batch ${b.id}`} · 남은 ${b.available}장`,
        app: a,
        marker: 'expiry',
      })
    }
    if (a.free_until) {
      const title = `무료 종료 · ${a.name}`
      out.push({ key: `free-${a.id}`, day: a.free_until, time: '', label: title, title, tone: tone('free_until'), detail: '이날까지 앱 자체가 무료', app: a, marker: 'free_until' })
    }
    if (a.ended_at) {
      const t = new Date(a.ended_at)
      const title = `나눔 종료 · ${a.name}`
      out.push({ key: `end-${a.id}`, day: dayKey(t), time: timeOf(t), label: title, title, tone: tone('ended'), detail: a.end_note, app: a, marker: 'ended' })
    }
  }
  return out
}

/** 일정 — 달력. Both programmes on one month grid with the code dates; a day
 *  opens a panel under the grid to move what is on it or put a waiting post
 *  there. The panel was a 24rem column on the right, which squeezed the month
 *  and wrapped every entry; under the grid it has the page's width. On a
 *  phone the grid becomes one week.
 *
 *  `now` is a prop so a test can pin the month; the page never passes it. */
export default function ScheduleCalendar({ now: nowProp }: { now?: Date }) {
  useWidePage()
  const now = useMemo(() => nowProp ?? new Date(), [nowProp])
  const desktop = useIsDesktop()
  const today = dayKey(now)
  const filters = useScheduleFilters()
  const [monthParam, setMonthParam] = useQueryParam('month', '')
  const [selected, setSelected] = useState(today)
  const [writing, setWriting] = useState(false)

  const m = /^(\d{4})-(\d{2})$/.exec(monthParam)
  const cursor = m ? { year: Number(m[1]), month: Number(m[2]) - 1 } : { year: now.getFullYear(), month: now.getMonth() }
  const setCursor = (c: { year: number; month: number }) =>
    setMonthParam(c.year === now.getFullYear() && c.month === now.getMonth() ? '' : `${c.year}-${pad(c.month + 1)}`)

  // The month; on a phone, widened to the selected day's week so the strip is
  // always filled.
  const week = weekDays(selected)
  const monthStart = new Date(cursor.year, cursor.month, 1)
  const monthEnd = new Date(cursor.year, cursor.month + 1, 1)
  const weekStartD = parseDay(week[0])
  const weekEndD = new Date(parseDay(week[6]).getTime() + 86_400_000)
  const from = !desktop && weekStartD < monthStart ? weekStartD : monthStart
  const to = !desktop && weekEndD > monthEnd ? weekEndD : monthEnd

  const base = {
    source: filters.source,
    appId: filters.appId || undefined,
    campaignId: filters.campaignId || undefined,
    platform: filters.platform || undefined,
  }
  const feed = useSchedule({ ...base, from: from.toISOString(), to: to.toISOString() }, { live: true })
  // What a day can take: everything waiting for a slot, and every failed post.
  const open = useSchedule({ ...base, status: 'unscheduled,failed' })

  const withMarkers = filters.source !== 'campaign' && !filters.campaignId && !filters.platform
  // The sources the grid can hold, as the feed reads the filters: an app is
  // the desk's and a campaign is the campaigns', so either one narrows it too.
  // The legend names only these, or a 리딤-only month reads as mixed.
  const shown: Source[] = [
    ...(filters.source !== 'campaign' && !filters.campaignId ? (['redeem'] as const) : []),
    ...(filters.source !== 'redeem' && !filters.appId ? (['campaign'] as const) : []),
  ]
  const appsQ = useRedeemApps()
  const apps = useMemo(() => (appsQ.data?.items ?? []).map((i) => i.app), [appsQ.data])
  const details = useQueries({
    queries: (withMarkers ? apps.filter((a) => a.status !== 'archived') : []).map((a) => ({
      queryKey: qk.redeemApp(a.id),
      queryFn: () => api.redeemApp(a.id),
    })),
  })
  const batches = useMemo(() => {
    const out: Record<number, RedeemBatch[]> = {}
    for (const d of details) if (d.data) out[d.data.app.id] = d.data.batches ?? []
    return out
  }, [details])

  const entries = useMemo(() => {
    const list: Entry[] = []
    for (const it of feed.data?.items ?? []) {
      const e = postEntry(it)
      if (e) list.push(e)
    }
    if (withMarkers) list.push(...markerEntries(apps, batches, filters.appId))
    return list.sort((x, y) => x.day.localeCompare(y.day) || x.time.localeCompare(y.time))
  }, [feed.data, apps, batches, filters.appId, withMarkers])

  const byDay = useMemo(() => {
    const map = new Map<string, Entry[]>()
    for (const e of entries) map.set(e.day, [...(map.get(e.day) ?? []), e])
    return map
  }, [entries])

  const monthKey = `${cursor.year}-${pad(cursor.month + 1)}`
  const inMonth = entries.filter((e) => e.day.startsWith(monthKey))
  const count = (pred: (e: Entry) => boolean) => inMonth.filter(pred).length
  const waiting = (open.data?.items ?? []).filter((i) => isWaiting(i) || i.status === 'failed')

  const shift = (by: number) => {
    const t = new Date(cursor.year, cursor.month + by, 1)
    setCursor({ year: t.getFullYear(), month: t.getMonth() })
  }
  const pick = (day: string) => {
    setSelected(day)
    const d = parseDay(day)
    if (d.getFullYear() !== cursor.year || d.getMonth() !== cursor.month) {
      setCursor({ year: d.getFullYear(), month: d.getMonth() })
    }
  }
  const shiftWeek = (by: number) => {
    const d = parseDay(selected)
    pick(dayKey(new Date(d.getFullYear(), d.getMonth(), d.getDate() + by * 7)))
  }

  return (
    <>
      <PageHeader
        title="일정"
        actions={
          <Button variant="primary" icon={PenLine} onClick={() => setWriting(true)}>
            글 쓰기
          </Button>
        }
      >
        <ScheduleFilterRow
          view="calendar"
          filters={filters}
          end={
            desktop ? (
              <div className="flex items-center gap-1">
                <IconButton label="이전 달" icon={ChevronLeft} size="sm" onClick={() => shift(-1)} />
                <span className="min-w-[6rem] text-center text-table font-medium tabular-nums">
                  {cursor.year}. {cursor.month + 1}
                </span>
                <IconButton label="다음 달" icon={ChevronRight} size="sm" onClick={() => shift(1)} />
                <Button
                  size="sm"
                  onClick={() => {
                    setCursor({ year: now.getFullYear(), month: now.getMonth() })
                    setSelected(today)
                  }}
                >
                  오늘
                </Button>
              </div>
            ) : undefined
          }
        />
        <p className="text-meta text-muted-foreground">
          {cursor.year}년 {cursor.month + 1}월 — 예약 {count((e) => e.item?.status === 'scheduled' || e.item?.status === 'approved')} · 게시됨{' '}
          {count((e) => e.item?.status === 'published')} · 실패 {count((e) => e.item?.status === 'failed')}
          {withMarkers && ` · 코드 만료 ${count((e) => e.marker === 'expiry')}`}
          {waiting.length > 0 && ` · 예약을 기다리는 글 ${waiting.length}`}
        </p>
      </PageHeader>

      {feed.error != null && (
        <ErrorState title="일정을 불러오지 못했다" error={feed.error} onRetry={() => void feed.refetch()} className="mb-4" />
      )}

      <div className="space-y-4">
        {desktop ? (
          <MonthCalendar
            year={cursor.year}
            month={cursor.month}
            today={today}
            selected={selected}
            onSelect={setSelected}
            entries={byDay}
          />
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-table font-medium">
              <span className="tabular-nums">
                {cursor.year}. {cursor.month + 1}
              </span>
              <Button size="sm" onClick={() => pick(today)}>
                오늘
              </Button>
            </div>
            <WeekStrip
              days={week}
              today={today}
              selected={selected}
              onSelect={pick}
              entries={byDay}
              onPrev={() => shiftWeek(-1)}
              onNext={() => shiftWeek(1)}
            />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          {(['scheduled', 'publishing', 'published', 'failed'] as const).map((k) => (
            <StatusPill key={k} domain="post" status={k} />
          ))}
          {withMarkers &&
            (['expiry', 'free_until', 'ended'] as const).map((k) => <StatusPill key={k} domain="marker" status={k} />)}
          <span className="ml-2 inline-flex items-center gap-1.5 text-micro text-muted-foreground">
            {shown.map((src) => (
              <SourceTag key={src} source={src} />
            ))}{' '}
            왼쪽 띠 색이 출처
          </span>
        </div>
        <DayPanel day={selected} entries={byDay.get(selected) ?? []} waiting={waiting} />
      </div>


      <WritePostDialog
        open={writing}
        onClose={() => setWriting(false)}
        initial={filters.source === 'campaign' ? 'campaign' : 'redeem'}
        desk={(done) => <DeskPostForm onDone={done} initialAppId={filters.appId || undefined} />}
        campaign={(done) => <DraftForm onDone={done} />}
      />
    </>
  )
}

/** The chosen day in full: each entry with its controls, then a way to put
 *  one of the waiting posts — either programme's — on this day. */
function DayPanel({ day, entries, waiting }: { day: string; entries: Entry[]; waiting: ScheduleItem[] }) {
  const commands = usePostCommands()
  const actions = usePostActions()
  const toast = useToast()
  const [pickKey, setPickKey] = useState('')
  const [time, setTime] = useState('09:00')
  const chosen = waiting.find((p) => `${p.kind}-${p.id}` === pickKey) ?? waiting[0]
  const tooEarly = pastSlotError(fromLocalInput(`${day}T${time}`))

  const book = async () => {
    if (!chosen) return
    const iso = fromLocalInput(`${day}T${time}`)
    if (!iso) return
    try {
      await actions.schedule.mutateAsync({ ref: refOf(chosen), at: iso })
      toast.success(`${describeDay(day)} ${time}에 예약했다`)
    } catch (e) {
      toast.error(errorText(e))
    }
  }

  return (
    <section aria-label={describeDay(day)} className="space-y-3 rounded-lg border border-border bg-card p-4">
      <h2 className="text-section font-semibold">
        {describeDay(day)}
        {entries.length > 0 && <span className="ml-2 text-table font-normal text-muted-foreground">{entries.length}건</span>}
      </h2>

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        {entries.length === 0 ? (
          <p className="text-meta text-muted-foreground">이날은 비어 있다.</p>
        ) : (
          <ul className="grid gap-2 xl:grid-cols-2">
            {entries.map((e) => (
              <li key={e.key} className="space-y-1.5 rounded-md border border-border p-2.5">
                <div className="flex flex-wrap items-center gap-2 text-meta">
                  {e.item ? (
                    <>
                      <SourceTag source={sourceOf(e.item)} />
                      <StatusPill domain="post" status={e.item.status} scheduledAt={e.item.scheduled_at} />
                    </>
                  ) : (
                    <TonePill tone={e.tone} label={statusOf('marker', e.marker ?? 'ended').label} />
                  )}
                  {e.time && <span className="tabular-nums text-muted-foreground">{e.time}</span>}
                  <span className="min-w-0 truncate font-medium text-foreground">
                    {e.item ? dayPanelWhere(e.item) : e.label}
                  </span>
                </div>
                {(e.detail || e.item?.media) && (
                  <div className="flex items-start gap-2">
                    {e.detail && <p className="line-clamp-2 min-w-0 flex-1 text-table text-muted-foreground">{e.detail}</p>}
                    {e.item?.media && (
                      <img
                        src={`/api/media/${e.item.media.id}/file`}
                        alt=""
                        loading="lazy"
                        className="ml-auto h-10 w-14 shrink-0 rounded border border-border object-cover"
                      />
                    )}
                  </div>
                )}
                {e.item && <EntryControls key={`${e.key}-${e.item.scheduled_at}`} it={e.item} day={day} commands={commands} />}
                {e.app && !e.item && (
                  <Link to={`/redeem/${e.app.id}`} className="block text-meta font-medium text-accent hover:underline">
                    앱 열기 →
                  </Link>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="space-y-2 border-t border-border pt-3 lg:border-l lg:border-t-0 lg:pl-4 lg:pt-0">
          <h3 className="text-meta font-semibold text-muted-foreground">이날에 예약</h3>
          {waiting.length === 0 ? (
            <p className="text-meta text-muted-foreground">예약을 기다리는 글이 없다. 위 &lsquo;글 쓰기&rsquo;로 쓴다.</p>
          ) : (
            <div className="space-y-2">
              <Select
                aria-label="예약할 초안"
                value={chosen ? `${chosen.kind}-${chosen.id}` : ''}
                onChange={(ev) => setPickKey(ev.target.value)}
                className="h-8 text-table"
              >
                {waiting.map((p) => (
                  <option key={`${p.kind}-${p.id}`} value={`${p.kind}-${p.id}`}>
                    {p.kind === 'redeem' ? '리딤' : '캠페인'} · {channelLabel(p)} · {ownerName(p)}
                    {p.status === 'failed' ? ' (실패)' : ''} — {headline(p, 40)}
                  </option>
                ))}
              </Select>
              <div className="flex items-center gap-2">
                <TimePicker label="예약 시각" value={time} onChange={setTime} size="sm" />
                <Button
                  size="sm"
                  variant="primary"
                  loading={actions.schedule.isPending}
                  disabled={!chosen || !time || !!tooEarly}
                  title="이 시각에 발행 크론이 게시한다"
                  onClick={() => void book()}
                >
                  예약
                </Button>
              </div>
              {tooEarly && (
                <p role="alert" className="text-meta text-status-failed">
                  {tooEarly}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      {commands.dialog}
    </section>
  )
}

/** What one post on the chosen day can do from here: a scheduled one can move
 *  or come off the queue; any opens its detail. */
function EntryControls({ it, day, commands }: { it: ScheduleItem; day: string; commands: ReturnType<typeof usePostCommands> }) {
  const open = useOpenDetail()
  const [slot, setSlot] = useState(() => {
    const t = it.scheduled_at ? new Date(it.scheduled_at) : null
    return t ? `${day}T${timeOf(t)}` : `${day}T09:00`
  })
  const can = abilities(it)
  const movable = it.status === 'scheduled' && can.schedule
  const iso = fromLocalInput(slot)
  const tooEarly = pastSlotError(iso)
  return (
    <div className="flex flex-wrap items-end gap-2">
      {movable && (
        <>
          <div className="space-y-1.5">
            <div className="text-meta font-medium">예약 시각 변경</div>
            <SlotPicker label="예약 시각 변경" value={slot} onChange={setSlot} size="sm" />
          </div>
          <Button size="sm" disabled={!iso || !!tooEarly} onClick={() => iso && void commands.schedule(refOf(it), iso)}>
            예약 변경
          </Button>
          <Button size="sm" variant="ghost" onClick={() => commands.askUnschedule(refOf(it), whereLabel(it))}>
            예약 취소
          </Button>
          {tooEarly && (
            <p role="alert" className="basis-full text-meta text-status-failed">
              {tooEarly}
            </p>
          )}
        </>
      )}
      <div className={cn('flex items-center gap-1', movable && 'ml-auto')}>
        {it.external_url && (
          <a
            href={it.external_url}
            target="_blank"
            rel="noreferrer"
            title={it.external_url}
            className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-meta font-medium text-accent hover:bg-muted"
          >
            <ExternalLink size={14} aria-hidden />글 보기
          </a>
        )}
        <Button size="sm" variant="ghost" icon={PanelRightOpen} onClick={() => open(refOf(it))}>
          열기
        </Button>
      </div>
    </div>
  )
}
